import type { AssetUploadUrlRead, AssetUploadUrlRequest } from '../types/api';
import { apiClient } from './apiClient';
import { supabase } from './supabase';

/** Private Supabase Storage bucket holding every business-owned upload. */
export const ASSETS_BUCKET = 'assets';

/**
 * Private bucket the design agent writes rendered carousel slides to. Separate
 * from `assets`: nothing the user uploaded lives here, and nothing here is
 * editable — a slide is replaced by re-rendering it.
 */
export const RENDERED_SLIDES_BUCKET = 'rendered-slides';

/** Buckets this client reads. Both are private; every read is signed. */
export type StorageBucket = typeof ASSETS_BUCKET | typeof RENDERED_SLIDES_BUCKET;

/** Client-side upload ceiling. The bucket enforces its own limit server-side. */
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/**
 * Accepted image MIME types. SVG is deliberately excluded — it is an active
 * document format and the renderer only needs raster art.
 */
export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;

/** Value for an `<input type="file" accept="...">` attribute. */
export const IMAGE_ACCEPT_ATTRIBUTE = ACCEPTED_IMAGE_TYPES.join(',');

/** How long a generated preview URL stays valid, in seconds. */
const SIGNED_URL_TTL_SECONDS = 60 * 60;

/** Renew a cached signed URL this many ms before it actually expires. */
const SIGNED_URL_REFRESH_MARGIN_MS = 5 * 60 * 1000;

interface CachedSignedUrl {
  url: string;
  expiresAt: number;
}

const signedUrlCache = new Map<string, CachedSignedUrl>();

/** Human-readable size for validation messages. */
function formatMegabytes(bytes: number): string {
  return `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`;
}

/**
 * Strips directory separators and anything outside a conservative charset.
 * The API builds the object key under the business prefix; this only keeps
 * the name it is handed tidy.
 */
export function sanitizeFileName(fileName: string): string {
  const collapsed = fileName.trim().toLowerCase().replace(/\\/g, '/').split('/').pop();
  const cleaned = (collapsed ?? '').replace(/[^a-z0-9._-]+/g, '-').replace(/^[-.]+/, '');
  return cleaned.slice(0, 80) || 'upload';
}

/** Returns a rejection message, or null when the file is acceptable. */
export function validateImageFile(file: File): string | null {
  if (!(ACCEPTED_IMAGE_TYPES as readonly string[]).includes(file.type)) {
    return 'That file type is not supported. Upload a PNG, JPEG, WebP or GIF image.';
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return `That file is ${formatMegabytes(file.size)}. Keep uploads under ${formatMegabytes(
      MAX_UPLOAD_BYTES,
    )}.`;
  }
  return null;
}

/**
 * Uploads one image and returns the object key to record on the asset row.
 *
 * The API is the only door into the bucket: it checks the byte size against
 * the account's storage cap, then signs a one-off upload URL. The bytes still
 * go straight to Storage (the API never proxies them), and a refused upload
 * never sends a byte.
 *
 * A 402 `PLAN_LIMIT_REACHED` from the API is rethrown as the `ApiError` it is,
 * so callers can say which cap is full (`planErrorMessage`).
 *
 * `collection` names the route that signs the upload: `assets` keys the
 * object under the brand's root, `inspirations` under its `inspo/` prefix.
 */
export async function uploadImageToAssets(
  businessId: string,
  file: File,
  collection: 'assets' | 'inspirations' = 'assets',
): Promise<string> {
  const validationMessage = validateImageFile(file);
  if (validationMessage) {
    throw new Error(validationMessage);
  }

  const body: AssetUploadUrlRequest = {
    filename: sanitizeFileName(file.name),
    content_type: file.type,
    size_bytes: file.size,
  };
  const { storage_path: storagePath, token } = await apiClient.post<AssetUploadUrlRead>(
    `/api/v1/businesses/${businessId}/${collection}/upload-url`,
    { body },
  );

  const { error } = await supabase.storage
    .from(ASSETS_BUCKET)
    .uploadToSignedUrl(storagePath, token, file, { contentType: file.type, upsert: false });

  if (error) {
    throw new Error(`Upload failed: ${error.message}`);
  }

  return storagePath;
}

/**
 * Signed preview URL for a private object. Cached per bucket + path until
 * shortly before expiry so a grid of assets does not re-sign on every render.
 *
 * The bucket defaults to `assets`, so upload-side callers are unchanged; the
 * rendered carousel slides pass `rendered-slides`.
 */
export async function createSignedAssetUrl(
  storagePath: string,
  bucket: StorageBucket = ASSETS_BUCKET,
): Promise<string> {
  const cacheKey = `${bucket}/${storagePath}`;
  const cached = signedUrlCache.get(cacheKey);
  if (cached && cached.expiresAt - SIGNED_URL_REFRESH_MARGIN_MS > Date.now()) {
    return cached.url;
  }

  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(storagePath, SIGNED_URL_TTL_SECONDS);

  if (error || !data?.signedUrl) {
    throw new Error(error?.message ?? 'Could not sign that file for preview.');
  }

  signedUrlCache.set(cacheKey, {
    url: data.signedUrl,
    expiresAt: Date.now() + SIGNED_URL_TTL_SECONDS * 1000,
  });

  return data.signedUrl;
}

/** Last path segment of an object key, for display in lists. */
export function assetFileName(storagePath: string): string {
  const segment = storagePath.split('/').pop() ?? storagePath;
  // Strip the uuid prefix added at upload time.
  return segment.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i, '');
}
